/**
 * The Whiteboard's tracking rules — pure, unit-tested (whiteboardTracking.test.ts).
 *
 * #3078 (White Board 2026-09-22): "a persistent tracked list of overdue /
 * past-date whiteboard items instead of letting them disappear when their date
 * passes." #2662: "define how overdue / past-deadline whiteboard items are
 * tracked". The definition, in one place:
 *
 *   An ENTRY (a goal or milestone, whiteboard-items) is OVERDUE when it is
 *   still open and its due date has passed — or, when it has no due date, the
 *   end of the week it was planned for. It stays on the Overdue list, whatever
 *   week the board is showing, until someone marks it done, gives it a new
 *   date, or drops it. A BLOCKER is not "due": it is open until it is cleared,
 *   and is tracked by how long it has been open (Slipping).
 *
 * A week NOTE's colour (a cell marked "Deadline / late") is a note about that
 * week, not a dated commitment, and makes nothing overdue: a note carried
 * forward week after week would otherwise list once per week it was written.
 *
 * Blake's Slipping view (Working Session 2026-10-16: "clear delivery dates and
 * visibility into workflow blockers") ranks the Delivery board's lanes, worst
 * first — see `slippingLanes`.
 *
 * Days are 'YYYY-MM-DD' on the browser's own calendar, the same clock the
 * board's "This week" column uses (whiteboard.types.ts currentWeekStart).
 */
import type { LaneWork, WhiteboardItemRecord, WhiteboardRowRecord } from "./whiteboard.types";

const DAY_MS = 24 * 60 * 60 * 1000;

const parseDay = (day: string): number => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : NaN;
};

/** Today on the browser's calendar, 'YYYY-MM-DD'. */
export function todayLocal(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

/** `day` moved by `days` (negative for earlier). */
export function addDays(day: string, days: number): string {
  const ms = parseDay(day);
  return Number.isNaN(ms) ? day : new Date(ms + days * DAY_MS).toISOString().slice(0, 10);
}

/** Whole days from `from` to `to` (positive when `to` is later). NaN on junk. */
export function daysBetween(from: string, to: string): number {
  return Math.round((parseDay(to) - parseDay(from)) / DAY_MS);
}

/** The Friday of a week (`weekOf` is its Monday). */
export const fridayOf = (weekOf: string): string => addDays(weekOf, 4);

/**
 * The due day a new entry starts with: its week's Friday, but never a day
 * already gone — logging "this week" on a Saturday must not make the entry
 * overdue the moment it is saved.
 */
export const defaultDueFor = (weekOf: string, today: string = todayLocal()): string => {
  const friday = fridayOf(weekOf);
  return friday < today ? today : friday;
};

/** The day an entry is due: its own date, or the last day of its week. */
export function effectiveDue(item: Pick<WhiteboardItemRecord, "dueDate" | "weekOf">): string {
  return item.dueDate || addDays(item.weekOf, 6);
}

export const isLiveItem = (item: WhiteboardItemRecord): boolean => !item.deletedAt;

export const isOpenItem = (item: WhiteboardItemRecord): boolean => isLiveItem(item) && item.status === "open";

/** Overdue, per the definition above. */
export function isOverdue(item: WhiteboardItemRecord, today: string): boolean {
  return isOpenItem(item) && item.kind !== "blocker" && effectiveDue(item) < today;
}

/** How many days an overdue entry is past its due day (0 when it is not overdue). */
export function daysOverdue(item: WhiteboardItemRecord, today: string): number {
  return isOverdue(item, today) ? Math.max(0, daysBetween(effectiveDue(item), today)) : 0;
}

/** Every overdue entry, the longest overdue first. */
export function overdueItems(items: readonly WhiteboardItemRecord[], today: string): WhiteboardItemRecord[] {
  return items
    .filter((item) => isOverdue(item, today))
    .sort((a, b) => effectiveDue(a).localeCompare(effectiveDue(b)) || a.title.localeCompare(b.title));
}

/** An open blocker's age in days: since the day it was logged. */
export function blockerAgeDays(item: WhiteboardItemRecord, today: string): number {
  const since = (item.createdAt ?? "").slice(0, 10);
  const age = daysBetween(since, today);
  return Number.isFinite(age) ? Math.max(0, age) : 0;
}

/** "today", "1 day", "12 days". */
export const dayCount = (days: number): string => (days === 0 ? "today" : days === 1 ? "1 day" : `${days} days`);

// ---- My week (#3383) -------------------------------------------------------

export interface MyWeek {
  /** This week's entries (every status), in lane order then by due day. */
  thisWeek: WhiteboardItemRecord[];
  /** Open entries from earlier weeks that are overdue. */
  overdue: WhiteboardItemRecord[];
  /** Open blockers from any week before this one (this week's are in `thisWeek`). */
  blockers: WhiteboardItemRecord[];
}

/** The signed-in person's week. `ownerId` null (nobody signed in) gives nothing. */
export function myWeek(
  items: readonly WhiteboardItemRecord[],
  ownerId: number | null,
  week: string,
  today: string
): MyWeek {
  if (ownerId === null) return { thisWeek: [], overdue: [], blockers: [] };
  const mine = items.filter((item) => isLiveItem(item) && Number(item.ownerUserId) === ownerId);
  const byDue = (a: WhiteboardItemRecord, b: WhiteboardItemRecord) =>
    effectiveDue(a).localeCompare(effectiveDue(b)) || a.title.localeCompare(b.title);
  return {
    thisWeek: mine.filter((item) => item.weekOf === week).sort(byDue),
    overdue: mine.filter((item) => item.weekOf < week && isOverdue(item, today)).sort(byDue),
    blockers: mine.filter((item) => item.weekOf < week && item.kind === "blocker" && isOpenItem(item)).sort(byDue),
  };
}

// ---- Slipping (Blake, 2026-10-16) -------------------------------------------

export interface SlippingLane {
  row: WhiteboardRowRecord;
  /** Days past the target date (> 0), or null when it is not past. */
  daysLate: number | null;
  /** Days until the target date (0 = today) when it is within the horizon, else null. */
  daysLeft: number | null;
  openBlockers: WhiteboardItemRecord[];
  overdueCount: number;
  /** The client's accepted action items past their due date (#2516). */
  pastDueWork: number;
  /** 0 past its date, 1 blocked or behind, 2 due soon: the order's first key. */
  tier: 0 | 1 | 2;
}

/** The horizons the view offers for "within N days of its date". */
export const SLIPPING_HORIZONS = [7, 14, 30] as const;

/**
 * The Delivery lanes that are slipping, worst first.
 *
 * A lane is listed when it is PAST its target date, has open BLOCKERS, has
 * OVERDUE entries, has accepted action items PAST DUE, or its target date is
 * within `horizonDays`. The order:
 *
 *   1. past its target date — the most days late first;
 *   2. then lanes that are blocked or behind — the most open blockers, overdue
 *      entries and past-due action items together first, then the oldest
 *      blocker;
 *   3. then lanes due soon — the soonest first.
 *
 * A lane that is several of these sorts by the worst. Ties go to the title.
 */
export function slippingLanes(input: {
  rows: readonly WhiteboardRowRecord[];
  items: readonly WhiteboardItemRecord[];
  laneWork: (clientId: string) => LaneWork | null;
  today: string;
  horizonDays: number;
}): SlippingLane[] {
  const { rows, items, laneWork, today, horizonDays } = input;
  const blockersByLane = new Map<string, WhiteboardItemRecord[]>();
  const overdueByLane = new Map<string, number>();
  for (const item of items) {
    if (item.kind === "blocker" && isOpenItem(item)) {
      const list = blockersByLane.get(item.rowId) ?? [];
      list.push(item);
      blockersByLane.set(item.rowId, list);
    } else if (isOverdue(item, today)) {
      overdueByLane.set(item.rowId, (overdueByLane.get(item.rowId) ?? 0) + 1);
    }
  }

  const listed: SlippingLane[] = [];
  for (const row of rows) {
    if (row.deletedAt || row.board !== "delivery") continue;
    const until = row.targetDate ? daysBetween(today, row.targetDate) : NaN;
    const daysLate = Number.isFinite(until) && until < 0 ? -until : null;
    const daysLeft = Number.isFinite(until) && until >= 0 && until <= horizonDays ? until : null;
    const openBlockers = (blockersByLane.get(row.id) ?? []).sort(
      (a, b) => String(a.createdAt ?? "").localeCompare(String(b.createdAt ?? "")),
    );
    const overdueCount = overdueByLane.get(row.id) ?? 0;
    const pastDueWork = row.clientId ? (laneWork(row.clientId)?.pastDue ?? 0) : 0;
    const behind = openBlockers.length > 0 || overdueCount > 0 || pastDueWork > 0;
    if (daysLate === null && daysLeft === null && !behind) continue;
    const tier: SlippingLane["tier"] = daysLate !== null ? 0 : behind ? 1 : 2;
    listed.push({ row, daysLate, daysLeft, openBlockers, overdueCount, pastDueWork, tier });
  }

  const trouble = (lane: SlippingLane) => lane.openBlockers.length + lane.overdueCount + lane.pastDueWork;
  const oldestBlocker = (lane: SlippingLane) => String(lane.openBlockers[0]?.createdAt ?? "9999");
  return listed.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier;
    if (a.tier === 0 && a.daysLate !== b.daysLate) return (b.daysLate ?? 0) - (a.daysLate ?? 0);
    if (a.tier !== 2 && trouble(a) !== trouble(b)) return trouble(b) - trouble(a);
    if (a.tier === 1) {
      const byAge = oldestBlocker(a).localeCompare(oldestBlocker(b));
      if (byAge !== 0) return byAge;
    }
    if (a.tier === 2 && a.daysLeft !== b.daysLeft) return (a.daysLeft ?? 0) - (b.daysLeft ?? 0);
    return (a.row.title || "").localeCompare(b.row.title || "");
  });
}
